import { RefObject, useEffect, useState } from 'react';

const CONSENT_HEADING_SELECTOR = '.gc-dg-tab-content > div > h4';
const SUMMARY_LINE_SELECTOR = "[data-gc-dg-element='gc-dg-summary-total'] p";
const PAYMENT_START_DATE = /^Payment start date:\s*(.+)$/;

export type ConsentBox = {
  checkboxId: string;
  headingSlot: HTMLElement;
  labelSlot: HTMLElement;
  detailsSlot: HTMLElement;
  brandPrivacyUrl?: string;
  dgPrivacyUrl?: string;
};

const createSlot = (className: string) => {
  const slot = document.createElement('div');
  slot.className = className;
  return slot;
};

const prepareConsentBox = (box: HTMLElement): ConsentBox | null => {
  const heading = box.querySelector(':scope > h4');
  const checkboxRow = box.querySelector("[id^='gc-dg-tandc-container-']");
  const label = checkboxRow?.querySelector(':scope > label');
  const checkbox = checkboxRow?.querySelector('input[type="checkbox"]');
  if (!heading || !label || !checkbox) return null;

  const [, brandPrivacyLink, dgPrivacyLink] = label.querySelectorAll('a');

  const headingSlot = createSlot('checkout-v2__consent-heading');
  const labelSlot = createSlot('checkout-v2__consent-label');
  const detailsSlot = createSlot('checkout-v2__consent-details');
  heading.before(headingSlot);
  label.after(labelSlot);
  box.append(detailsSlot);

  return {
    checkboxId: checkbox.id,
    headingSlot,
    labelSlot,
    detailsSlot,
    brandPrivacyUrl: brandPrivacyLink?.getAttribute('href') ?? undefined,
    dgPrivacyUrl: dgPrivacyLink?.getAttribute('href') ?? undefined,
  };
};

/**
 * @description Adds slots to the SDK consent boxes (one per payment method) for the layout's own
 * heading, label and details, and takes the payment start date out of the order summary.
 * @returns the prepared consent boxes, and the payment start date read from the summary
 */
export const useConsentBoxes = (panelRef: RefObject<HTMLElement | null>) => {
  const [consentBoxes, setConsentBoxes] = useState<ConsentBox[]>([]);
  const [paymentStartDate, setPaymentStartDate] = useState<string>();

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const prepared = new Map<Element, ConsentBox>();

    const scan = () => {
      let hasChanged = false;
      panel.querySelectorAll(CONSENT_HEADING_SELECTOR).forEach((heading) => {
        const box = heading.parentElement;
        if (!box || prepared.has(box)) return;
        const consentBox = prepareConsentBox(box);
        if (!consentBox) return;
        prepared.set(box, consentBox);
        hasChanged = true;
      });
      prepared.forEach((_, box) => {
        if (panel.contains(box)) return;
        prepared.delete(box);
        hasChanged = true;
      });
      if (hasChanged) setConsentBoxes([...prepared.values()]);

      panel.querySelectorAll<HTMLElement>(SUMMARY_LINE_SELECTOR).forEach((line) => {
        const match = line.textContent?.trim().match(PAYMENT_START_DATE);
        if (!match) return;
        line.hidden = true;
        setPaymentStartDate(match[1]);
      });
    };

    const observer = new MutationObserver(scan);
    observer.observe(panel, { childList: true, subtree: true });
    scan();

    return () => observer.disconnect();
  }, [panelRef]);

  return { consentBoxes, paymentStartDate };
};
